import React from 'react';
import { useIntl } from 'react-intl';
import { ToggleButtonGroup } from '@mui/material';
import { ToggleButton } from '@linetweet/linetweet-ui';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArchiveIcon from '@mui/icons-material/Archive';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { FilterSortBarProps, SortMode, WarningFilter } from '../types';
import styles from './FilterSortBar.module.scss';

const WARNING_LABEL_KEYS: Record<WarningFilter, string> = {
  missingOutcome: 'seriesbooking.haOverview.filter.missingOutcome',
  missingFollowup: 'seriesbooking.haOverview.filter.missingFollowup',
  ratioExceeded: 'seriesbooking.haOverview.filter.ratioExceeded',
};

const WARNING_GROUP_CLASS: Record<WarningFilter, string> = {
  missingOutcome: styles.warningMissingOutcome,
  missingFollowup: styles.warningMissingFollowup,
  ratioExceeded: styles.warningRatioExceeded,
};

const WARNING_OPTIONS: WarningFilter[] = ['missingOutcome', 'missingFollowup', 'ratioExceeded'];

export function FilterSortBar({
  sortMode,
  onSortModeChange,
  archived,
  archivedCount,
  onArchivedToggle,
  warnings,
  warningCounts,
  onWarningChange,
}: FilterSortBarProps) {
  const intl = useIntl();

  return (
    <div className={styles.bar}>
      <div className={styles.leftGroup}>
        <ToggleButtonGroup
          exclusive
          spacing={1}
          value={sortMode}
          className={styles.sortGroup}
          onChange={(_event, value: SortMode | null) => {
            if (value) {
              onSortModeChange(value);
            }
          }}
        >
          <ToggleButton value="alpha" aria-label="Sort A-Z">
            <SortByAlphaIcon />
          </ToggleButton>
          <ToggleButton value="recent" aria-label="Sort by recent">
            <AccessTimeIcon />
          </ToggleButton>
        </ToggleButtonGroup>

        <div className={styles.divider} />

        <ToggleButtonGroup value={archived ? ['archived'] : []} className={styles.archiveGroup}>
          <ToggleButton
            value="archived"
            startIcon={<ArchiveIcon />}
            onClick={onArchivedToggle}
          >
            {intl.formatMessage(
              { id: 'seriesbooking.haOverview.archived' },
              { count: archivedCount },
            )}
          </ToggleButton>
        </ToggleButtonGroup>
      </div>

      <div className={styles.rightGroup}>
        {WARNING_OPTIONS.map((warning) => {
          const active = warnings.includes(warning);
          return (
            <ToggleButtonGroup
              key={warning}
              value={active ? [warning] : []}
              className={WARNING_GROUP_CLASS[warning]}
            >
              <ToggleButton
                value={warning}
                startIcon={<WarningAmberIcon />}
                onClick={() => {
                  if (active) {
                    onWarningChange(warnings.filter((value) => value !== warning));
                  } else {
                    onWarningChange([...warnings, warning]);
                  }
                }}
              >
                {intl.formatMessage(
                  { id: WARNING_LABEL_KEYS[warning] },
                  { count: warningCounts[warning] },
                )}
              </ToggleButton>
            </ToggleButtonGroup>
          );
        })}
      </div>
    </div>
  );
}
